import { Divider, FileItem, Skeleton, Typography } from "@codat/orchard-ui";
import { ExcelDownloadMetaDataResponse } from "api/queries/useGetExcelDownloadMetaDataQuery";
import React from "react";
import formatDate from "utils/formatDate";

import Button from "components/Button";

import styles from "./ExportDataModal.module.scss";

export interface ExportDataModalProps {
  companyId: string;
  metadata?: ExcelDownloadMetaDataResponse;
  metadataLoading: boolean;
  isGeneratingReport: boolean;
  generateNewReport: () => Promise<void>;
  handleExcelFileDownload: () => Promise<void>;
}

export const ExportDataModal: React.FC<ExportDataModalProps> = ({
  companyId,
  handleExcelFileDownload,
  generateNewReport,
  isGeneratingReport,
  metadata,
  metadataLoading,
}: ExportDataModalProps) => {
  function handleClick() {
    generateNewReport();
  }

  return (
    <div className={styles.modal} data-testid="export-data-modal">
      <Typography variant="heading_xl" as="h2">
        Export data
      </Typography>
      <Typography as="p" className={styles.smallText} variant="small">
        Generate a new file or download last available report
      </Typography>
      <div className={styles.buttonWrapper}>
        <Button
          buttonName="Create new data export"
          label="Create new data export"
          eventProperties={{ companyId }}
          data-testid="create-new-report-button"
          isLoading={isGeneratingReport}
          onClick={() => {
            handleClick();
          }}
          spinnerAlign="left"
          isSkeleton={!isGeneratingReport && metadataLoading}
        />
        <Typography as="p" className={styles.smallText} variant="small">
          Export accounting and banking data shared by the company
        </Typography>
      </div>
      <Divider className={styles.divider} />
      <div className={styles.reportsWrapper}>
        <Typography variant="body_sm" as="h3">
          Last export
        </Typography>
        {metadata?.lastGenerated && (
          <FileItem
            className={styles.fileItem}
            id={metadata?.lastGenerated}
            file={
              {
                name: metadata?.lastGenerated
                  ? `${"Data export"} - ${formatDate(metadata.lastGenerated)}`
                  : "Unknown file",
              } as File
            }
            showFileSize={false}
            onDownloadClick={handleExcelFileDownload}
          />
        )}
        {(isGeneratingReport || !metadataLoading) &&
          !metadata?.lastGenerated && (
            <Typography className={styles.smallText} variant="body_sm" as="p">
              {isGeneratingReport
                ? "Generating report..."
                : "No file generated."}
            </Typography>
          )}
        {!isGeneratingReport && metadataLoading && (
          <Skeleton height="1.625rem" width="100%" />
        )}
      </div>
    </div>
  );
};
